'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { CategoryMetadata } from '@lib/types/category';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { buildCategoryHierarchy } from '@lib/util/buildCategoryHierarchy';
import {
  getCategoryDisplayName,
  getCategoryIconName,
} from '@lib/util/category';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { usePathname, useSearchParams } from 'next/navigation';
import { DynamicIcon } from 'phosphor-icons/react';
import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TreeAccordion, TreeNodeData } from 'ui-components';
import { FacetAccordion } from '../../FacetAccordion';
import { useSearchControllers } from '../headless/SearchDefinitionContext';
import { createSingleSelectFacetHrefBuilder } from '../headless/facetHref';
import { CategoryFacetProps } from './CategoryFacet.types';

const CATEGORY_DELIMITER = '>';

/** Converts a Coveo category value ("seg1 > seg2") to a tree path ID ("seg1/seg2"). */
function coveoValueToTreeId(value: string): string {
  return value
    .split(CATEGORY_DELIMITER)
    .map((s) => s.trim())
    .join('/');
}

/**
 * buildCategoryHierarchy synthesizes an absent ancestor from the first
 * descendant that needs one, so such a row carries a deeper coveoValue than
 * the category it represents; selecting it would filter to that descendant
 * instead.
 */
function isSynthesizedAncestor(metadata: CategoryMetadata): boolean {
  return (
    metadata.coveoValue.split(CATEGORY_DELIMITER).length !==
    metadata.fullPath.length
  );
}

function enhanceTreeWithIconsAndLabels(
  nodes: TreeNodeData<CategoryMetadata>[],
  searchValuesMicrocopy: MicrocopyItem[] | undefined,
  selectedIds: string[],
  activeValue: string | undefined,
  buildHref: (value: string | null) => string,
  parentPath: string[] = []
): TreeNodeData<CategoryMetadata>[] {
  return nodes.map((node) => {
    const currentPath = [...parentPath, node.id];
    // Re-key each node by its full path ("2d/characters"): buildCategoryHierarchy
    // keys nodes by bare segment, but the tree needs path ids so they're unique
    // across branches and match coveoValueToTreeId(selected value) for the
    // active-row highlight and expansion seeding.
    const id = currentPath.join('/');
    const iconName = getCategoryIconName(node.id);
    // The selected value legitimately comes back with a zero count once
    // another facet or the query eliminates its matches; disabling it then
    // would strand the selection, since re-clicking the active row is the
    // only way to clear a category (it gets no breadcrumb chip, and a
    // category-only selection renders no breadcrumb bar to Clear from).
    // Same rule as the flat SSR facets.
    const disabled = node.disabled && !selectedIds.includes(id);
    // A link must reach what a click does: the active row clears, and rows a
    // click ignores get none.
    const href =
      disabled || !node.metadata || isSynthesizedAncestor(node.metadata)
        ? undefined
        : buildHref(
            node.metadata.coveoValue === activeValue
              ? null
              : node.metadata.coveoValue
          );

    return {
      ...node,
      id,
      label: getCategoryDisplayName(node.id, searchValuesMicrocopy),
      disabled,
      href,
      icon: iconName
        ? createElement(DynamicIcon, {
            iconName,
            size: 20,
            className: 'text-blue',
          })
        : undefined,
      children: node.children
        ? enhanceTreeWithIconsAndLabels(
            node.children,
            searchValuesMicrocopy,
            selectedIds,
            activeValue,
            buildHref,
            currentPath
          )
        : undefined,
    };
  });
}

/** Every ancestor path of the given tree ids ("a/b/c" → "a", "a/b"). */
function seedExpansion(treeIds: string[]): string[] {
  const ancestors = treeIds.flatMap((id) => {
    const parts = id.split('/');
    return parts.slice(0, -1).map((_, i) => parts.slice(0, i + 1).join('/'));
  });
  return Array.from(new Set(ancestors));
}

function findNodeById(
  items: TreeNodeData<CategoryMetadata>[],
  targetId: string
): TreeNodeData<CategoryMetadata> | null {
  for (const item of items) {
    if (item.id === targetId) return item;
    if (item.children) {
      const found = findNodeById(item.children, targetId);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Category navigation tree — a single-select tree over the `ec_category` facet.
 *
 * Unlike the v1 category facet (multi-select with implicit parent→descendant
 * coverage),
 * only one category is active at a time: selecting a node filters results to
 * that category and its descendants — the multi-value field matches every
 * asset under the path — via the facet's single-select; selecting another one
 * replaces the selection, and selecting the active one again clears it, each
 * in exactly one search.
 *
 * The facet's `state.values` mirror the last *response*, so the active
 * category is read from the search parameter manager instead: it is
 * request-synchronous and the same source the URL is written from. The value
 * handed to `toggleSingleSelect` gets its `state` from that truth too —
 * otherwise a click during an in-flight search re-selects the previous
 * category on top of the new one.
 *
 * The full tree is always rendered and freely expandable (expansion is
 * display-only, independent of selection), and selecting a category never
 * prunes it — a Coveo facet doesn't filter its own values. The tree is built
 * from the facet values for the current query, so it reflects which categories
 * have results, rather than from a separate static taxonomy.
 *
 * Each row is also a link to the URL its selection reaches, so hover shows the
 * target and modifier clicks, middle clicks and the context menu open or copy
 * the server-rendered category page; a plain click stays on
 * `toggleSingleSelect`.
 */
export const CategoryFacet = ({
  variant = 'sidebar',
  className,
  isOpen,
  onToggle,
}: CategoryFacetProps) => {
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const categoryTitle = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.categories',
    'Categories'
  );

  const controllers = useSearchControllers();
  const { state, methods } = controllers.useCategoryFacet();
  const { state: searchParameterState } =
    controllers.useSearchParameterManager();

  const pathname = usePathname() || '/';
  const searchParams = useSearchParams();

  // `methods` only lands once hydrateStaticState resolves. Until then — or for
  // good, if hydration fails — the rows are plain links to the server-rendered
  // category URL; once it lands a plain click runs the single-search toggle.
  const isReady = methods !== undefined;

  const treeData = useMemo(() => {
    if (state.values.length === 0) return [];
    return buildCategoryHierarchy(state.values, CATEGORY_DELIMITER);
  }, [state.values]);

  // The single active category (if any), per the current request.
  const activeValue: string | undefined =
    searchParameterState.parameters.f?.[state.facetId]?.[0];
  const selectedIds = useMemo(
    () => (activeValue ? [coveoValueToTreeId(activeValue)] : []),
    [activeValue]
  );

  const buildHref = useMemo(
    () =>
      createSingleSelectFacetHrefBuilder({
        parameters: searchParameterState.parameters,
        facetId: state.facetId,
        pathname,
        search: searchParams?.toString() ?? '',
      }),
    [searchParameterState.parameters, state.facetId, pathname, searchParams]
  );

  const enhancedTreeData = useMemo(
    () =>
      enhanceTreeWithIconsAndLabels(
        treeData,
        searchValuesMicrocopy,
        selectedIds,
        activeValue,
        buildHref
      ),
    [treeData, searchValuesMicrocopy, selectedIds, activeValue, buildHref]
  );

  // Seeded during the first render rather than from an effect, so a
  // deep-linked selection is already expanded in the server-rendered HTML
  // instead of only after hydration.
  const [expandedIds, setExpandedIds] = useState<string[]>(() =>
    seedExpansion(selectedIds)
  );
  const seededSelectionKey = useRef(selectedIds.join('|'));

  // Re-seeds whenever the active category itself changes: a selection that only
  // appears after the first render (a client-side navigation into a filtered
  // state, where the values arrive with the search that follows), or one
  // restored by Back after its branch was collapsed — otherwise the only
  // selected row would stay unmounted, leaving the selection invisible and
  // unclearable. Keyed on the selection rather than a one-shot flag so an
  // unrelated search never re-expands a branch the user has since collapsed,
  // and merged into the current set so other open branches survive.
  useEffect(() => {
    const selectionKey = selectedIds.join('|');
    if (selectionKey === seededSelectionKey.current) return;
    seededSelectionKey.current = selectionKey;
    if (selectedIds.length === 0) return;

    const seeded = seedExpansion(selectedIds);
    if (seeded.length === 0) return;

    setExpandedIds((prev) => {
      const missing = seeded.filter((id) => !prev.includes(id));
      return missing.length === 0 ? prev : [...prev, ...missing];
    });
  }, [selectedIds]);

  const handleExpandedChange = useCallback((newExpandedIds: string[]) => {
    setExpandedIds((prev) => {
      const collapsedIds = prev.filter((id) => !newExpandedIds.includes(id));
      if (collapsedIds.length === 0) return newExpandedIds;

      // When collapsing a parent, also drop its now-hidden descendants.
      const filtered = newExpandedIds.filter(
        (id) =>
          !collapsedIds.some((collapsed) => id.startsWith(collapsed + '/'))
      );
      return filtered.length === newExpandedIds.length
        ? newExpandedIds
        : filtered;
    });
  }, []);

  const handleSelect = useCallback(
    (id: string) => {
      const node = findNodeById(enhancedTreeData, id);
      if (!node?.metadata) return;
      const value = state.values.find(
        (v) => v.value === node.metadata!.coveoValue
      );
      if (!value || isSynthesizedAncestor(node.metadata)) return;
      // Single-select: selects this category (replacing any other) in one
      // search, or clears it if it is the active one.
      const isActive = value.value === activeValue;
      methods?.toggleSingleSelect({
        ...value,
        state: isActive ? 'selected' : 'idle',
      });
    },
    [enhancedTreeData, state.values, methods, activeValue]
  );

  if (state.values.length === 0) {
    return null;
  }

  return (
    <FacetAccordion
      title={categoryTitle}
      variant={variant === 'horizontal' ? 'sidebar' : variant}
      className={className}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedIds.length}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <TreeAccordion<CategoryMetadata>
        items={enhancedTreeData}
        isDarkTheme={variant === 'mobile'}
        selectionMode="single"
        // Single-select keys the active row on selectedId; selectedIds is kept
        // for the header count and the expansion seeding above.
        selectedId={selectedIds[0]}
        selectedIds={selectedIds}
        expandedIds={expandedIds}
        onExpandedChange={handleExpandedChange}
        onSelect={isReady ? handleSelect : undefined}
        linkRel="nofollow"
        size="default"
      />
    </FacetAccordion>
  );
};
